import { Clock } from '@phosphor-icons/react';

import { cycleDayNumber, cycleDaysRemaining, cycleTotalDays } from '../../utils/cycleDays';
import { isCycleCloseDue, isCycleCloseOverdue } from '../../utils/cycleCloseAlert';
import { HeroProgressBar } from '../common/HeroProgressBar';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { DashboardCycleCloseBanner } from './DashboardCycleCloseBanner';

function formatSpineDate(iso) {
  if (!iso) return null;
  const date = new Date(`${iso}T12:00:00`);
  return new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'short' }).format(date);
}

/**
 * Z1 — `.pen` → `ds/card/cycle-header` (`NbO2i`).
 *
 * Pilha vertical `gap $space-2` com:
 * 1. `spine` — cartão `$surface-card` / `$border` / `$radius-ds-lg` cujo
 *    conteúdo (`pad`: `$space-4` topo, `$space-5` laterais, 0 em baixo) é uma
 *    linha `cyc-top` alinhada ao fundo: à esquerda o eyebrow `$type-section` +
 *    as datas em `$font-mono` `$type-label`; à direita o relógio 16 + a leitura
 *    `$type-display-stat`. A barra de tempo (`track`, 4px) fica encostada à
 *    base do cartão, a toda a largura.
 * 2. `banner-slot` — o banner de fecho de ciclo (desligado por omissão no
 *    master, ligado nos frames «Z1 urgente»).
 *
 * O `.pen` não recolore a leitura nem a barra no estado urgente — a urgência é
 * comunicada pelo banner e pela copy («fecha hoje» / «está por fechar»).
 */
export function DashboardCycleHeader({ cycle, remindersEnabled = true, className }) {
  const daysLeft = cycleDaysRemaining(cycle);
  const dayNumber = cycleDayNumber(cycle);
  const totalDays = cycleTotalDays(cycle);

  const urgent = remindersEnabled && isCycleCloseDue(cycle);
  const overdue = urgent && isCycleCloseOverdue(cycle);

  let readout = null;
  if (urgent) {
    readout = overdue
      ? pt.dashboard.cycleCard.readoutOverdue
      : pt.dashboard.cycleCard.readoutToday;
  } else if (daysLeft === 1) {
    readout = pt.dashboard.cycleCard.readoutOneDay;
  } else if (daysLeft != null) {
    readout = pt.dashboard.cycleCard.readoutDays.replace('{days}', String(daysLeft));
  }

  const elapsedPct =
    dayNumber != null && totalDays ? Math.min(100, Math.max(0, (dayNumber / totalDays) * 100)) : 0;
  const fillPct = urgent ? 100 : elapsedPct;
  const startLabel = formatSpineDate(cycle?.start);
  const endLabel = formatSpineDate(cycle?.end);

  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-space-2', className)}>
      <div className="flex min-w-0 flex-col overflow-hidden rounded-ds-lg border border-border bg-card">
        <div className="flex min-w-0 flex-col px-space-5 pt-space-4">
          <div className="flex min-w-0 items-end justify-between gap-space-4 pb-space-4">
            <div className="flex min-w-0 flex-col gap-space-1">
              <p className="type-section text-muted-foreground">
                {pt.dashboard.cycleCard.title}
              </p>
              {startLabel && endLabel ? (
                <div className="flex min-w-0 items-center gap-space-2 font-mono text-type-label font-normal text-muted-foreground">
                  <span>{startLabel}</span>
                  <span className="opacity-50" aria-hidden>
                    —
                  </span>
                  <span>{endLabel}</span>
                </div>
              ) : null}
            </div>

            {readout ? (
              <div className="flex shrink-0 items-center gap-space-2 text-foreground">
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                <p className="type-display-stat">{readout}</p>
              </div>
            ) : null}
          </div>
        </div>

        {startLabel && endLabel ? (
          <HeroProgressBar
            variant="surface"
            size="sm"
            percentage={fillPct}
            ariaLabel={pt.dashboard.cycleCard.title}
          />
        ) : null}
      </div>

      <DashboardCycleCloseBanner cycle={cycle} remindersEnabled={remindersEnabled} />
    </div>
  );
}

export default DashboardCycleHeader;
